import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Flag as FlagIcon, GraduationCap } from 'lucide-react'

import PinGate, { clearAdminSession } from './PinGate.jsx'
import {
  PbccHeroHeader,
  PbccAdminSidebar,
  PbccSectionTitle,
  PbccQuickActions,
} from '../components/PbccLayout.jsx'
import { trialSignOut } from '../auth/TrialGate.jsx'
import AIAssistant from '../components/AIAssistant.jsx'
import DemoLauncher from './DemoLauncher.jsx'
import PracticeLauncher from './PracticeLauncher.jsx'
import { GpsTestModal } from './AdminApp.jsx'
import CLUB from '../config/club.js'

// Dedicated Demos & Practice page — its own /admin/demos route + sidebar
// entry. Reuses the same DemoLauncher / PracticeLauncher launchers and card
// styling as the home dashboard's Demos & Practice section, so the two stay
// visually identical while this one gets its own destination.
export default function DemosPractice() {
  const navigate = useNavigate()
  const [showGpsTest, setShowGpsTest] = useState(false)
  const [demoMode, setDemoMode] = useState(null)
  const [demoSize, setDemoSize] = useState(96)
  return (
    <PinGate>
      {/* Content only — AdminShell provides the sidebar + zoom frame. */}
      <>
        <div className="pbcc-main-panel-4ball flex-1 flex flex-col min-h-0 min-w-0">
          <PbccHeroHeader
            variant="admin"
            showCommandCenter={false}
            onLock={() => { clearAdminSession(); location.reload() }}
            onSignOut={() => { clearAdminSession(); trialSignOut() }}
          />
          <div className="flex flex-1 min-h-0">
            <main className="max-w-6xl w-full mx-auto p-4 sm:p-5 space-y-6 flex flex-col flex-1 min-h-0">
              <PbccSectionTitle
                title="Demos & Practice"
                subtitle="Try prebuilt examples or walk through setup with sample data — nothing here affects your real tournaments."
              />

              <div className="grid md:grid-cols-2 gap-3">
                <div className="pbcc-demo-card">
                  <div
                    className="pbcc-demo-card-photo relative"
                    style={{
                      backgroundImage: `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.15)), url(/pbcc-fountain.png)`,
                      backgroundPosition: 'center',
                    }}>
                    <DemoBadge Icon={FlagIcon} />
                  </div>
                  <div className="pbcc-demo-card-body">
                    <h3 className="font-extrabold text-base text-gray-900">Try a Demo</h3>
                    <p className="text-xs text-gray-600 mt-1 mb-3 leading-snug">
                      See a full tournament running with prebuilt teams and scores. Best for showing off live leaderboard, TV view, and the GPS map.
                    </p>
                    <ul className="pbcc-checklist space-y-1 mb-3">
                      <CheckLi>View live leaderboard</CheckLi>
                      <CheckLi>TV view &amp; GPS map</CheckLi>
                      <CheckLi>Perfect for showing features</CheckLi>
                    </ul>
                    <DemoLauncher
                      buttonClass="pbcc-primary-btn w-full"
                      onDemoLaunched={(key, result) => {
                        if (key === 'fullTournament' || key === 'strokePlay') {
                          setDemoMode(key)
                          setDemoSize(result?.size || result?.teamRows?.length || 96)
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="pbcc-demo-card">
                  <div
                    className="pbcc-demo-card-photo relative"
                    style={{
                      backgroundImage: `linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.25)), url(/pbcc-flag.png)`,
                      backgroundPosition: 'center',
                    }}>
                    <DemoBadge Icon={GraduationCap} />
                  </div>
                  <div className="pbcc-demo-card-body">
                    <h3 className="font-extrabold text-base text-gray-900">Practice Setup</h3>
                    <p className="text-xs text-gray-600 mt-1 mb-3 leading-snug">
                      Walk through creating a tournament with sample data so you can edit, reset, and try again before doing it for real.
                    </p>
                    <ul className="pbcc-checklist space-y-1 mb-3">
                      <CheckLi>Edit, reset, and test</CheckLi>
                      <CheckLi>Safe practice environment</CheckLi>
                      <CheckLi>No impact to real tournaments</CheckLi>
                    </ul>
                    <PracticeLauncher buttonClass="pbcc-primary-btn w-full" />
                  </div>
                </div>
              </div>

              <div className="mt-auto">
                <PbccQuickActions
                  onCreate={() => navigate('/admin')}
                  onGpsTest={() => setShowGpsTest(true)}
                />
              </div>
            </main>
          </div>
        </div>
      </>
      {showGpsTest ? <GpsTestModal onClose={() => setShowGpsTest(false)} /> : null}
      <AIAssistant tournamentId={null} demoMode={demoMode} demoSize={demoSize} />
    </PinGate>
  )
}

function CheckLi({ children }) {
  return (
    <li className="flex items-start gap-2 text-xs leading-snug text-gray-600">
      <Check size={13} strokeWidth={2.5} style={{ color: CLUB.accentColor, marginTop: 2 }} />
      <span>{children}</span>
    </li>
  )
}

function DemoBadge({ Icon }) {
  return (
    <div
      className="absolute left-3 bottom-3 flex items-center justify-center rounded-full"
      style={{
        width: 36,
        height: 36,
        background: CLUB.primaryColor,
        border: `2px solid ${CLUB.accentColor}`,
        color: '#FFFFFF',
        boxShadow: '0 4px 12px rgba(0,0,0,0.28)',
      }}>
      <Icon size={16} strokeWidth={2} />
    </div>
  )
}
